import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Add, DeleteOutline, Refresh, Save } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const defaultFilters = {
  start: '',
  end: '',
  provider: '',
  model: '',
  status: ''
};

function formatNumber(value) {
  return new Intl.NumberFormat('es-ES').format(Math.round(Number(value) || 0));
}

function formatCost(value) {
  const amount = Number(value) || 0;
  return `$${amount.toFixed(amount > 0 && amount < 0.01 ? 6 : 2)}`;
}

function formatDate(value) {
  if (!value) return '-';
  return new Intl.DateTimeFormat('es-ES', {
    dateStyle: 'short',
    timeStyle: 'short'
  }).format(new Date(value));
}

const metricCards = [
  { key: 'requests', label: 'Peticiones' },
  { key: 'totalTokens', label: 'Tokens totales' },
  { key: 'estimatedCostUsd', label: 'Coste estimado', format: formatCost },
  { key: 'errors', label: 'Errores' }
];

export default function AIUsageDashboard() {
  const { authToken } = useAuth();
  const [filters, setFilters] = useState(defaultFilters);
  const [data, setData] = useState(null);
  const [ratesData, setRatesData] = useState({ rates: [], discoveredModels: [] });
  const [rateForm, setRateForm] = useState({
    provider: 'gemini',
    model: 'gemini-3.5-flash',
    inputUsdPer1M: '',
    outputUsdPer1M: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [error, setError] = useState('');
  const [rateMessage, setRateMessage] = useState('');

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    return params.toString();
  }, [filters]);

  const loadUsage = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/ai-usage/summary?${queryString}`, {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.message || 'No se pudo cargar el consumo IA');
      }
      setData(payload.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadRates = async () => {
    setRatesLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/ai-usage/rates`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.message || 'No se pudieron cargar las tarifas IA');
      }
      setRatesData(payload.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setRatesLoading(false);
    }
  };

  const saveRate = async () => {
    setRateMessage('');
    setError('');
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/ai-usage/rates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
        body: JSON.stringify({
          ...rateForm,
          inputUsdPer1M: Number(rateForm.inputUsdPer1M),
          outputUsdPer1M: Number(rateForm.outputUsdPer1M)
        })
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.message || 'No se pudo guardar la tarifa');
      }
      setRateMessage(`Tarifa guardada. Logs recalculados: ${payload.data.recalculatedLogs}`);
      await Promise.all([loadRates(), loadUsage()]);
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteRate = async (rateId) => {
    setRateMessage('');
    setError('');
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/ai-usage/rates/${rateId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.message || 'No se pudo eliminar la tarifa');
      }
      setRateMessage(`Tarifa eliminada. Logs recalculados: ${payload.data.recalculatedLogs}`);
      await Promise.all([loadRates(), loadUsage()]);
    } catch (err) {
      setError(err.message);
    }
  };

  const pickModelForRate = (item) => {
    const existingRate = item.rate || {};
    setRateForm({
      provider: item.provider,
      model: item.model,
      inputUsdPer1M: existingRate.inputUsdPer1M ?? '',
      outputUsdPer1M: existingRate.outputUsdPer1M ?? '',
      notes: existingRate.notes || ''
    });
  };

  useEffect(() => {
    if (authToken) loadUsage();
  }, [authToken, queryString]);

  useEffect(() => {
    if (authToken) loadRates();
  }, [authToken]);

  const totals = data?.totals || {};

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b' }}>
            Consumo IA
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
            Trazabilidad por usuario, modelo y petición
          </Typography>
        </Box>
        <Button startIcon={<Refresh />} variant="outlined" onClick={loadUsage} disabled={loading}>
          Actualizar
        </Button>
      </Stack>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: 'none', border: '1px solid #e2e8f0' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              fullWidth
              label="Desde"
              type="date"
              value={filters.start}
              onChange={(event) => setFilters(prev => ({ ...prev, start: event.target.value }))}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              fullWidth
              label="Hasta"
              type="date"
              value={filters.end}
              onChange={(event) => setFilters(prev => ({ ...prev, end: event.target.value }))}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              select
              fullWidth
              label="Proveedor"
              value={filters.provider}
              onChange={(event) => setFilters(prev => ({ ...prev, provider: event.target.value }))}
              size="small"
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="gemini">Gemini</MenuItem>
              <MenuItem value="groq">Groq</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              fullWidth
              label="Modelo"
              value={filters.model}
              onChange={(event) => setFilters(prev => ({ ...prev, model: event.target.value }))}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              select
              fullWidth
              label="Estado"
              value={filters.status}
              onChange={(event) => setFilters(prev => ({ ...prev, status: event.target.value }))}
              size="small"
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="success">Correctas</MenuItem>
              <MenuItem value="error">Errores</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {rateMessage && <Alert severity="success" sx={{ mb: 3 }}>{rateMessage}</Alert>}

      {loading && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {metricCards.map(card => (
              <Grid item xs={12} sm={6} md={3} key={card.key}>
                <Paper sx={{ p: 2.5, borderRadius: 2, boxShadow: 'none', border: '1px solid #e2e8f0' }}>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>{card.label}</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
                    {card.format ? card.format(totals[card.key]) : formatNumber(totals[card.key])}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 'none', border: '1px solid #e2e8f0' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Tarifas por modelo
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      Precios en USD por 1M tokens. Al guardar se recalcula el histórico de ese modelo.
                    </Typography>
                  </Box>
                  <Button startIcon={<Refresh />} onClick={loadRates} disabled={ratesLoading}>
                    Recargar tarifas
                  </Button>
                </Stack>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField fullWidth label="Proveedor" value={rateForm.provider} onChange={(event) => setRateForm(prev => ({ ...prev, provider: event.target.value }))} size="small" />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth label="Modelo" value={rateForm.model} onChange={(event) => setRateForm(prev => ({ ...prev, model: event.target.value }))} size="small" />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField fullWidth label="Input USD / 1M" type="number" value={rateForm.inputUsdPer1M} onChange={(event) => setRateForm(prev => ({ ...prev, inputUsdPer1M: event.target.value }))} size="small" inputProps={{ min: 0, step: '0.000001' }} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField fullWidth label="Output USD / 1M" type="number" value={rateForm.outputUsdPer1M} onChange={(event) => setRateForm(prev => ({ ...prev, outputUsdPer1M: event.target.value }))} size="small" inputProps={{ min: 0, step: '0.000001' }} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Stack direction="row" spacing={1}>
                      <TextField fullWidth label="Notas" value={rateForm.notes} onChange={(event) => setRateForm(prev => ({ ...prev, notes: event.target.value }))} size="small" />
                      <Button variant="contained" startIcon={<Save />} onClick={saveRate}>
                        Guardar
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Modelo detectado</TableCell>
                        <TableCell align="right">Peticiones</TableCell>
                        <TableCell align="right">Tokens</TableCell>
                        <TableCell align="right">Input / 1M</TableCell>
                        <TableCell align="right">Output / 1M</TableCell>
                        <TableCell>Actualizado por</TableCell>
                        <TableCell align="right">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(ratesData.discoveredModels || []).map(item => (
                        <TableRow key={`${item.provider}/${item.model}`} hover>
                          <TableCell>{item.provider}/{item.model}</TableCell>
                          <TableCell align="right">{formatNumber(item.requests)}</TableCell>
                          <TableCell align="right">{formatNumber(item.totalTokens)}</TableCell>
                          <TableCell align="right">{item.rate ? formatCost(item.rate.inputUsdPer1M) : '-'}</TableCell>
                          <TableCell align="right">{item.rate ? formatCost(item.rate.outputUsdPer1M) : '-'}</TableCell>
                          <TableCell>{item.rate?.updatedBy?.email || '-'}</TableCell>
                          <TableCell align="right">
                            <Button size="small" startIcon={<Add />} onClick={() => pickModelForRate(item)}>
                              Editar
                            </Button>
                            {item.rate?._id && (
                              <Button size="small" color="error" startIcon={<DeleteOutline />} onClick={() => deleteRate(item.rate._id)}>
                                Quitar
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {!ratesData.discoveredModels?.length && (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 4, color: '#94a3b8' }}>
                            Aún no hay modelos detectados. Puedes crear una tarifa manual arriba.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

            <Grid item xs={12} lg={6}>
              <UsageTable
                title="Por usuario"
                columns={['Usuario', 'Peticiones', 'Tokens', 'Coste', 'Errores']}
                rows={(data?.byUser || []).map(item => [
                  item.email || item.userId || 'Sin usuario',
                  formatNumber(item.requests),
                  formatNumber(item.totalTokens),
                  formatCost(item.estimatedCostUsd),
                  formatNumber(item.errors)
                ])}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <UsageTable
                title="Por modelo y operación"
                columns={['Modelo', 'Operación', 'Peticiones', 'Tokens', 'Coste']}
                rows={(data?.byModel || []).map(item => [
                  `${item.provider}/${item.model}`,
                  item.operation,
                  formatNumber(item.requests),
                  formatNumber(item.totalTokens),
                  formatCost(item.estimatedCostUsd)
                ])}
              />
            </Grid>
            <Grid item xs={12}>
              <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 'none', border: '1px solid #e2e8f0' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Últimas peticiones
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Fecha</TableCell>
                        <TableCell>Usuario</TableCell>
                        <TableCell>Operación</TableCell>
                        <TableCell>Modelo</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell align="right">Tokens</TableCell>
                        <TableCell align="right">Latencia</TableCell>
                        <TableCell align="right">Coste</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(data?.recent || []).map(item => (
                        <TableRow key={item._id} hover>
                          <TableCell>{formatDate(item.createdAt)}</TableCell>
                          <TableCell>{item.user?.email || '-'}</TableCell>
                          <TableCell>{item.operation}</TableCell>
                          <TableCell>{item.provider}/{item.model}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={item.status === 'success' ? 'OK' : 'Error'}
                              color={item.status === 'success' ? 'success' : 'error'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">{formatNumber(item.tokens?.totalTokens)}</TableCell>
                          <TableCell align="right">{formatNumber(item.latencyMs)} ms</TableCell>
                          <TableCell align="right">{formatCost(item.estimatedCostUsd)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}

function UsageTable({ title, columns, rows }) {
  return (
    <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 'none', border: '1px solid #e2e8f0', height: '100%' }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        {title}
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map(column => <TableCell key={column}>{column}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={`${title}-${index}`} hover>
                {row.map((cell, cellIndex) => (
                  <TableCell key={`${title}-${index}-${cellIndex}`}>{cell}</TableCell>
                ))}
              </TableRow>
            ))}
            {!rows.length && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 4, color: '#94a3b8' }}>
                  Sin datos para estos filtros
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
